// client/src/components/pages/LexiStar/Billing/billingLogic.ts
//
// Pure helpers of the Usage & billing page and the statement document (ST-00207 stream A;
// m4b-contracts §2.3/§2.5): SAST months, the month choices, rand / count formatting, the
// drill-down links and the plan form; and (ST-00241) the flat monthly rule's wording — the
// "How was this calculated?" sentences, a truck's charge and subscription status, the notice
// form. The page computes no figure of its own — every amount is the server's statement line
// (m4-contracts §1 "figures are deterministic"); the notice form only previews the billing end
// date the server stores (notice date + 1 calendar month).

import type {
  AssetCharge,
  AssetsLine,
  BilledChannel,
  BillingBasis,
  FlatMonthlyAssetsLine,
  MessagesLine,
  PlanSnapshot,
  RemovalNotice,
  StatementLine,
  StatementSummary,
  SubscriptionAsset,
  TruckDaysAssetsLine,
} from '../../../../types/lexistar-billing';

export const TZ = 'Africa/Johannesburg';

const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

export const isMonth = (v: unknown): v is string => typeof v === 'string' && MONTH_RE.test(v);

/** Today's SAST month, 'YYYY-MM'. */
export function sastMonth(now: Date = new Date()): string {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone: TZ, year: 'numeric', month: '2-digit' }).formatToParts(now).map((x) => [x.type, x.value])
  );
  return `${p.year}-${p.month}`;
}

export function addMonths(month: string, n: number): string {
  const m = MONTH_RE.exec(month);
  if (!m) return month;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1 + n, 1)).toISOString().slice(0, 7);
}

export function daysInMonth(month: string): number {
  const m = MONTH_RE.exec(month);
  return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).getUTCDate() : 30;
}

export const firstDay = (month: string): string => `${month}-01`;
export const lastDay = (month: string): string => `${month}-${String(daysInMonth(month)).padStart(2, '0')}`;
/** 'YYYY-MM' of a 'YYYY-MM-DD'. */
export const monthOf = (date: string): string => date.slice(0, 7);

/** 'September 2026' */
export function monthLabel(month: string): string {
  const m = MONTH_RE.exec(month);
  if (!m) return month;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)).toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '26 Sep' for a 'YYYY-MM-DD' (fixed abbreviations: locale data differs between runtimes). */
export function dayLabel(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const name = m ? MONTH_SHORT[Number(m[2]) - 1] : undefined;
  return m && name ? `${Number(m[3])} ${name}` : date;
}

export interface MonthOption {
  value: string;
  label: string;
  status: StatementSummary['status'] | null;
}

/**
 * The months the page offers: the current SAST month and the 12 before it, newest first, plus
 * any older month that has a statement; each with its statement's status when there is one.
 */
export function monthOptions(current: string, statements: StatementSummary[] = []): MonthOption[] {
  const byMonth = new Map(statements.map((s) => [monthOf(s.period_month), s.status]));
  const months = new Set<string>();
  for (let i = 0; i <= 12; i += 1) months.add(addMonths(current, -i));
  for (const m of byMonth.keys()) if (m <= current) months.add(m);
  return [...months]
    .sort((a, b) => (a < b ? 1 : -1))
    .map((value) => {
      const status = byMonth.get(value) ?? null;
      return { value, status, label: `${monthLabel(value)}${status ? ` — ${status === 'final' ? 'final' : 'draft'}` : ''}` };
    });
}

// ── Formatting ─────────────────────────────────────────────────────────────────────

const group = (n: number, decimals: number): string =>
  n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

/** 'R 13,043.50' (the statement's own two decimals; a minus sign for credits). */
export function formatZar(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return `${n < 0 ? '−' : ''}R ${group(Math.abs(n), 2)}`;
}

/** Whole counts: '1,240'. */
export const formatCount = (v: number | null | undefined): string =>
  v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : group(Number(v), 0);

/** Active-truck units: '13.73'. */
export const formatUnits = (v: number | null | undefined): string =>
  v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : group(Number(v), 2);

/** A VAT fraction as a percentage: 0.15 -> '15 %', 0.155 -> '15.5 %'. */
export function formatVat(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(Number(rate))) return '—';
  const pct = Math.round(Number(rate) * 1000000) / 10000;
  return `${Number.isInteger(pct) ? pct : pct.toString()} %`;
}

export const isAssetsLine = (l: StatementLine): l is AssetsLine => l.kind === 'assets';
export const isMessagesLine = (l: StatementLine): l is MessagesLine => l.kind === 'messages';
/** The trucks line of the flat monthly rule (ST-00241); every statement before it has the truck-days line. */
export const isFlatAssetsLine = (l: StatementLine): l is FlatMonthlyAssetsLine => l.kind === 'assets' && l.basis === 'flat_monthly';
export const isTruckDaysLine = (l: StatementLine): l is TruckDaysAssetsLine => l.kind === 'assets' && l.basis !== 'flat_monthly';
export const flatLineOf = (lines: StatementLine[]): FlatMonthlyAssetsLine | null => lines.find(isFlatAssetsLine) ?? null;

/** A plan snapshot's basis: none stored = truck_days (statements before ST-00241). */
export const basisOfSnapshot = (p: Pick<PlanSnapshot, 'billing_basis'> | null | undefined): BillingBasis =>
  p?.billing_basis === 'flat_monthly' ? 'flat_monthly' : 'truck_days';

export const BASIS_LABELS: Record<BillingBasis, string> = {
  flat_monthly: 'Flat monthly rate per truck',
  truck_days: 'Active trucks (truck-days)',
};

/** '10 Oct 2026' for a 'YYYY-MM-DD'. */
export function dateLabel(date: string | null | undefined): string {
  if (!date) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return m ? `${dayLabel(date)} ${m[1]}` : date;
}

/** A date plus whole calendar months, the day clamped to the month's end (the server's date + 1 month). */
export function addCalendarMonths(date: string, n: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  const month = addMonths(`${m[1]}-${m[2]}`, n);
  const day = Math.min(Number(m[3]), daysInMonth(month));
  return `${month}-${String(day).padStart(2, '0')}`;
}

/** A notice's billing end date: the date it was received + 1 calendar month. */
export const billingEndsOn = (noticeReceivedOn: string): string => addCalendarMonths(noticeReceivedOn, 1);

/** The last month charged when billing stops before `billingEnds` ('YYYY-MM'). */
export function lastChargedMonth(billingEnds: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(billingEnds);
  if (!m) return monthOf(billingEnds);
  return m[3] === '01' ? addMonths(monthOf(billingEnds), -1) : monthOf(billingEnds);
}

/** 'First month: 22 of 31 days' / 'Full month' (+ ' · last month' when its notice ends billing). */
export function chargeLabel(a: Pick<AssetCharge, 'charge' | 'days' | 'days_in_month' | 'final_month'>): string {
  const base = a.charge === 'first_month' ? `First month: ${a.days} of ${a.days_in_month} days` : 'Full month';
  return a.final_month ? `${base} · last month` : base;
}

/** Short tags for a truck gone from the app without a written notice (still billed). */
export const REMOVED_LABELS = {
  asset_removed: 'Removed in the app, no notice',
  device_unlinked: 'Device unlinked, no notice',
} as const;

/** The trucks of a flat line that are gone from the app without a notice (still billed). */
export const removedWithoutNotice = (line: FlatMonthlyAssetsLine): AssetCharge[] => line.assets.filter((a) => a.removed !== null && !a.notice_id);

const plural = (n: number, one: string, many: string): string => `${formatCount(n)} ${n === 1 ? one : many}`;
const fullMonths = (n: number): string => (n === 0 ? 'no truck for the full month' : `${plural(n, 'truck', 'trucks')} for the full month`);
const firstMonths = (n: number): string =>
  n === 0 ? 'no truck in its first month' : n === 1 ? '1 truck in its first month' : `${formatCount(n)} trucks in their first month`;

/** The small text under the trucks line: '2 trucks for the full month + 1 truck in its first month, at the flat rate per truck'. */
export function flatLineDetail(line: Pick<FlatMonthlyAssetsLine, 'full_months' | 'first_months'>): string {
  const parts = [line.full_months > 0 ? fullMonths(line.full_months) : '', line.first_months > 0 ? firstMonths(line.first_months) : ''].filter(Boolean);
  return parts.length ? `${parts.join(' + ')}, at the flat rate per truck` : 'no truck on the subscription this month';
}

/**
 * "How was this calculated?" for a flat monthly trucks line, in plain sentences (ST-00241):
 * the rule, the activation date, the first month, the notice, then this month's sum — with
 * the statement's own first pro-rata truck as the worked example.
 */
export function flatExplanation(line: FlatMonthlyAssetsLine): string[] {
  const rate = formatZar(line.rate_zar);
  const example = line.assets.find((a) => a.charge === 'first_month');
  const out = [
    `Each truck on the subscription is charged the flat monthly rate of ${rate} for every month — whether it drove, stood parked or was in the workshop.`,
    'A truck joins the subscription on its activation date: the first day (South African time) on which its GPS device sent a valid position after the device was linked to it. A truck without a linked GPS device, or whose device has not sent a valid position yet, is not charged.',
    `Only a truck’s first month is charged in part: the days from its activation date to the end of that month ÷ the days in the month, to two decimals (half up).${
      example
        ? ` In this statement: ${example.registration || example.asset_id.slice(0, 8)}, active from ${dateLabel(example.activated_on)}: ${example.days} ÷ ${example.days_in_month} = ${formatUnits(example.fraction)} × ${rate} = ${formatZar(example.amount_zar)}.`
        : ''
    }`,
    'A truck leaves the subscription only with written notice: billing ends one calendar month after the date the notice was received, and every month that starts before then is charged in full. Deleting a truck in the app or unlinking its GPS device does not stop billing.',
    `This month: ${fullMonths(line.full_months)} and ${firstMonths(line.first_months)} = ${formatUnits(line.units)} trucks; the amount is the sum of the trucks’ amounts = ${formatZar(line.amount_zar)}.`,
  ];
  return out;
}

// ── The subscription and the notice form (ST-00241) ────────────────────────────────

/** Where a truck stands, in words (its subscription status). */
export function subscriptionStatusText(a: Pick<SubscriptionAsset, 'status' | 'notice'>): string {
  switch (a.status) {
    case 'no_device':
      return 'Not billed — no GPS device linked';
    case 'awaiting_fix':
      return 'Not billed yet — waiting for the first valid GPS position (checked every night)';
    case 'billable':
      return 'Billed every month';
    case 'notice_given':
      return a.notice
        ? `Notice received — billed up to and including ${monthLabel(lastChargedMonth(a.notice.billing_ends_on))}`
        : 'Notice received';
    case 'ended':
      return a.notice ? `No longer billed — billing ended ${dateLabel(a.notice.billing_ends_on)}` : 'No longer billed';
    default:
      return a.status;
  }
}

/** A live notice that may still be withdrawn on `today` (before its billing end date). */
export const canWithdraw = (n: Pick<RemovalNotice, 'withdrawn_at' | 'billing_ends_on'> | null, today: string): boolean =>
  !!n && !n.withdrawn_at && today < n.billing_ends_on;

export interface NoticeDraft {
  notice_received_on: string;
  reference: string;
  note: string;
}

export type NoticeDraftResult =
  | { ok: true; value: { notice_received_on: string; reference?: string; note?: string } }
  | { ok: false; error: string };

/** Check the typed notice the way the API does (POST /subscription/:assetId/notices). */
export function validateNoticeDraft(d: NoticeDraft, today: string): NoticeDraftResult {
  const date = d.notice_received_on.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m || Number(m[3]) < 1 || Number(m[3]) > daysInMonth(`${m[1]}-${m[2]}`)) return { ok: false, error: 'Choose the date the written notice arrived.' };
  if (date > today) return { ok: false, error: 'The notice cannot have arrived after today.' };
  if (d.reference.trim().length > 200) return { ok: false, error: 'The reference is at most 200 characters.' };
  if (d.note.trim().length > 2000) return { ok: false, error: 'The note is at most 2,000 characters.' };
  return {
    ok: true,
    value: {
      notice_received_on: date,
      ...(d.reference.trim() ? { reference: d.reference.trim() } : {}),
      ...(d.note.trim() ? { note: d.note.trim() } : {}),
    },
  };
}

export const CHANNEL_NAMES: Record<BilledChannel, string> = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email' };

/** The messaging cost log report for a month and channel (the drill-down behind a message line). */
export function messagingLogHref(month: string, channel: BilledChannel): string {
  const q = new URLSearchParams({ from: firstDay(month), to: lastDay(month), channels: channel });
  return `/lexistar/reports/messaging_cost_log?${q.toString()}`;
}

/** The printable statement page (the page the report engine prints). */
export function statementPageHref(month: string, tenantId?: number | null): string {
  return `/lexistar/billing/statement/${month}${tenantId ? `?tenant_id=${tenantId}` : ''}`;
}

// ── The plan form ──────────────────────────────────────────────────────────────────

export interface PlanDraft {
  plan_code: string;
  billing_basis: BillingBasis;
  asset_rate_zar: string;
  included_messages: string;
  /** Percent, as typed: '15'. */
  vat_percent: string;
}

export function draftOfPlan(p: { plan_code: string; billing_basis?: BillingBasis; asset_rate_zar: number; included_messages: number; vat_rate: number }): PlanDraft {
  return {
    plan_code: p.plan_code,
    billing_basis: p.billing_basis ?? 'flat_monthly',
    asset_rate_zar: String(p.asset_rate_zar),
    included_messages: String(p.included_messages),
    vat_percent: String(Math.round(p.vat_rate * 1000000) / 10000),
  };
}

export type PlanDraftResult =
  | { ok: true; value: { plan_code: string; billing_basis: BillingBasis; asset_rate_zar: number; included_messages: number; vat_rate: number } }
  | { ok: false; error: string };

/** Check the typed plan the way the API does (PUT /plan), with sentences a person can act on. */
export function validatePlanDraft(d: PlanDraft): PlanDraftResult {
  const code = d.plan_code.trim().toLowerCase();
  if (!/^[a-z][a-z0-9_-]{0,39}$/.test(code)) return { ok: false, error: 'The plan code is a short word: letters, digits, - or _, starting with a letter.' };
  if (d.billing_basis !== 'flat_monthly' && d.billing_basis !== 'truck_days') return { ok: false, error: 'Choose how the trucks are billed.' };
  const rate = Number(d.asset_rate_zar);
  if (d.asset_rate_zar.trim() === '' || !Number.isFinite(rate) || rate < 0) return { ok: false, error: 'Type the rate per truck per month in rand (0 or more).' };
  if (Math.round(rate * 100) / 100 !== rate) return { ok: false, error: 'The rate has at most two decimals (cents).' };
  const included = Number(d.included_messages);
  if (d.included_messages.trim() === '' || !Number.isInteger(included) || included < 0) {
    return { ok: false, error: 'Included messages is a whole number (0 or more).' };
  }
  const vat = Number(d.vat_percent);
  if (d.vat_percent.trim() === '' || !Number.isFinite(vat) || vat < 0 || vat >= 100) return { ok: false, error: 'VAT is a percentage from 0 to 99.99.' };
  const vatRate = Math.round(vat * 100) / 10000;
  return { ok: true, value: { plan_code: code, billing_basis: d.billing_basis, asset_rate_zar: rate, included_messages: included, vat_rate: vatRate } };
}
